/**
 * The warm-up ramp: how much a number is allowed to send today.
 *
 * A number that pairs on Monday and sends two hundred messages to strangers on Tuesday is
 * the single clearest ban pattern there is, and until this existed the product's only
 * defence was a three-step ladder — 20 a day for three days, 40 for a week, then 250 — which
 * meant a number's ceiling more than doubled overnight, twice. WhatsApp is watching the
 * derivative as much as the total, so the ladder is replaced here by a curve: the ceiling
 * grows a little every day for three weeks and never jumps.
 *
 * Kept pure and separate from `broadcast.ts` so that the one number in this product most
 * likely to lose a customer their WhatsApp account is a function with a test, rather than an
 * expression inside a route handler.
 *
 * The two axes are deliberately separate. This module owns *volume* — how many conversations
 * a number may start today. `broadcast.ts` owns *cadence* — how far apart they go. A number
 * can be allowed few messages at a wide spacing (brand new) or many at a narrow one
 * (established), and conflating the two would mean a cap change silently retimed the run.
 */

import type { NumberMaturity } from './broadcast'
import type { HealthTier, NumberHealth } from './numberHealth'

/** Where the ramp starts. Low enough to look like a person trying the product out. */
export const WARMUP_FIRST_DAY_CAP = 10

/** Where it ends: the ceiling an established number carries, unchanged from before. */
export const WARMUP_TARGET_CAP = 250

/**
 * How long it takes to get there. Three weeks is the shortest span in the circulating
 * warm-up guidance that nobody argues is reckless; it is also long enough that the daily
 * step is small at every point, which is the property that matters.
 */
export const WARMUP_DAYS = 21

const DAY_MS = 86_400_000

/**
 * The moment the ramp counts a number's age from, chosen so a reconnect can never move it.
 *
 * `connectedAt` is stamped once — the first time a number reports `connected` — and never
 * rewritten afterwards (see the session-host status route). `lastConnectedAt` is deliberately
 * NOT consulted: it is overwritten on every reconnect and every host redeploy, so a routine
 * socket drop this morning would otherwise reset a months-old number to warm-up day one, cap
 * ten. `createdAt` is the fallback for rows added before `connectedAt` was recorded; it too is
 * written once at row creation and never moves, so it holds the age stable rather than resetting
 * it. A number that has genuinely never connected has neither, and is treated as brand new.
 */
export function pairingMoment(row: {
  connectedAt: Date | null
  createdAt: Date | null
}): Date | null {
  return row.connectedAt ?? row.createdAt
}

/**
 * Which day of its warm-up a number is on, counting the pairing day as day 1.
 *
 * Calendar days since pairing, not days it was actually connected — an honest limitation.
 * Nothing in this product records a number's connected time, so a number that paired three
 * weeks ago and was offline for nineteen of those days finishes its ramp on schedule despite
 * having built no history at all. The health tier below is what partially covers that case.
 */
export function warmupDay(pairedAt: Date | null, now: Date): number {
  // A connected number with no recorded pairing time is a gap in our data, not evidence that
  // the number is seasoned, so it is treated as brand new.
  if (!pairedAt) return 1
  const elapsed = now.getTime() - pairedAt.getTime()
  if (elapsed < 0) return 1
  return Math.floor(elapsed / DAY_MS) + 1
}

/**
 * The ceiling on a given day of the ramp.
 *
 * Geometric rather than linear, because that is the shape of the guidance and of the risk:
 * the difference between 10 and 20 a day matters far more to a week-old number than the
 * difference between 200 and 210 does to a month-old one. It also lands close to the ladder
 * it replaces — around 14 on day 3 and 43 on day 10, against the old 20 and 40 — so this is
 * a smoothing of the existing policy rather than a new one nobody has thought about.
 */
export function warmupCap(day: number): number {
  if (day <= 1) return WARMUP_FIRST_DAY_CAP
  if (day >= WARMUP_DAYS) return WARMUP_TARGET_CAP

  const progress = (day - 1) / (WARMUP_DAYS - 1)
  const growth = WARMUP_TARGET_CAP / WARMUP_FIRST_DAY_CAP
  return Math.round(WARMUP_FIRST_DAY_CAP * Math.pow(growth, progress))
}

/**
 * What each health tier is allowed to do with its ramp ceiling.
 *
 * `at_risk` is zero, not a small number. A number whose sends are being refused, or which is
 * cycling through reconnects, does not need a gentler campaign — it needs to stop starting
 * conversations until it is well, and the recipients it would have taken are deferred to the
 * next window rather than failed. This is what turns number health from a chip in the
 * settings screen into something with an effect.
 */
export const HEALTH_ALLOWANCE: Record<HealthTier, number> = {
  healthy: 1,
  watch: 0.5,
  at_risk: 0,
}

export interface WarmupPlan {
  /** 1-based day of the ramp. */
  day: number
  /** Today's ceiling before health is taken into account. */
  rampCap: number
  /** Today's actual ceiling. */
  cap: number
  complete: boolean
  tier: HealthTier
  /** Plain-language explanation, or `null` when the number is simply at full strength. */
  reason: string | null
}

export function warmupPlan(input: {
  pairedAt: Date | null
  now: Date
  health: NumberHealth
}): WarmupPlan {
  const day = warmupDay(input.pairedAt, input.now)
  const rampCap = warmupCap(day)
  const tier = input.health.tier
  const cap = Math.floor(rampCap * HEALTH_ALLOWANCE[tier])
  const complete = day >= WARMUP_DAYS

  let reason: string | null = null
  if (cap === 0) {
    reason = `This number is not in a state to start new conversations: ${input.health.reason}`
  } else if (cap < rampCap) {
    reason =
      `Today's limit is halved to ${cap} while this number settles. ` + input.health.reason
  } else if (!complete) {
    reason =
      `Day ${day} of ${WARMUP_DAYS} of warm-up, so this number may start ${cap} new ` +
      'conversations today. The limit grows every day.'
  }

  return { day, rampCap, cap, complete, tier, reason }
}

/**
 * Everything the pacing engine needs about one number: how fast to send, and how much.
 *
 * Composed here rather than in the route so that "what may this number do today" has exactly
 * one answer, arrived at the same way for a broadcast sent now, a broadcast scheduled for
 * next Tuesday, and the preview that quotes the customer a figure before either.
 */
export function pacingFor(input: {
  pairedAt: Date | null
  maturity: NumberMaturity
  health: NumberHealth
  now: Date
}): { maturity: NumberMaturity; cap: number; warmup: WarmupPlan } {
  const warmup = warmupPlan({ pairedAt: input.pairedAt, now: input.now, health: input.health })
  return { maturity: input.maturity, cap: warmup.cap, warmup }
}
